// apps/web/src/pages/author/UnsavedChangesGuard.tsx
//
// Phase 8 Plan 07 Task 2 — UnsavedChangesGuard (authr-01).
//
// Intercepts route navigation when the recipe form is dirty. Mounts a Dialog
// with a destructive "Leave and discard changes" Confirm + a safe outline
// "Stay on page" Cancel that is the default-focused button (UI-SPEC line 888:
// destructive Confirm is NEVER the default; pressing ENTER on the Dialog
// cancels (safe), not confirms (destructive)).
//
// Source verbatim from 08-RESEARCH.md §"UnsavedChangesGuard with v6
// useBlocker" lines 1099-1129.
//
// IMPORTANT: useBlocker is a data-router-only hook. The project's
// apps/web/src/router.tsx uses createBrowserRouter (data router) at line 47 —
// confirmed compatible. If a future refactor switches to <BrowserRouter>
// (declarative), this hook silently no-ops.
//
// FORMSTATE DISCIPLINE: reading useFormContext().formState here is allowed —
// the guard is a toolbar peer (mounted at EditorShell level), NOT a hot
// form-section subtree.
import { useFormContext } from "react-hook-form";
import { useBlocker } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function UnsavedChangesGuard(): React.ReactElement | null {
  const { formState } = useFormContext();
  const blocker = useBlocker(formState.isDirty);
  if (blocker.state !== "blocked") return null;
  return (
    <Dialog open={true} onOpenChange={() => blocker.reset?.()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Unsaved changes</DialogTitle>
        </DialogHeader>
        <p>You have unsaved changes that will be lost if you leave this page.</p>
        <DialogFooter>
          {/*
            Stay on page MUST be the auto-focused default per UI-SPEC line 888.
            Radix Dialog autoFocus + render-order: the outline button is
            rendered first in the JSX and carries `autoFocus` to make sure ENTER
            on the Dialog triggers the safe action, not the destructive one.
          */}
          <Button type="button" variant="outline" autoFocus onClick={() => blocker.reset?.()}>
            Stay on page
          </Button>
          <Button type="button" variant="destructive" onClick={() => blocker.proceed?.()}>
            Leave and discard changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
